// gioco/usaFiltri.ts — collega il motore dei filtri alla partita: lo crea quando MediaPipe è pronto, applica il
// filtro scelto (se chi gioca può usarlo) e lo ricorda sul telefono.
// v046: ricorda quanto è potente il telefono (filtri/potenza.ts): sui telefoni lenti i cambi automatici evitano i
// filtri pesanti e, se il telefono va in affanno anche alla qualità più bassa con un filtro pesante, si passa da soli a
// un filtro leggero simile (`cambioFluido`, la partita mostra un messaggio positivo). Chi non ha mai scelto un filtro
// gioca con un filtro «di benvenuto» (leggero, gratis, buffo), che non si salva come scelta.
import { useCallback, useEffect, useRef, useState } from "react";
import { MotoreFiltri } from "../filtri/motore.ts";
import { caricaFiltro } from "../filtri/registro.ts";
import { accessoFiltro } from "../filtri/accesso.ts";
import { ALZA_TESTA, impostaAlzaTesta, MARGINE_BOCCA } from "../filtri/geometria.ts";
import { STABILIZZA_PREDEFINITO } from "../filtri/stabilizza.ts";
import { filtriDisponibili, profiloFiltri, suCambioProfiloFiltri } from "../servizi/filtri.ts";
import { analisiSullaCpu, caricaAnalizzatori } from "./rilevamento.ts";
import { livelloIniziale } from "../filtri/qualita.ts";
import {
  filtriConsigliati,
  filtroAdatto,
  filtroDiBenvenuto,
  filtroFluidoSimile,
  impostaIndiziTelefono,
  impostaPesi,
  registraLivelloPartita,
  segnaAffanno,
  serveFiltroFluido,
  type VoceIndice,
} from "../filtri/potenza.ts";
import { segnalaErrore } from "../servizi/errori.ts";
import { tempoReale } from "../servizi/tempoReale.ts";
import { eBooleano, eNumero, leggiImpostazione } from "../servizi/impostazioni.ts";
import { leggiPreferenza, salvaPreferenza } from "../servizi/preferenze.ts";
import { IMP_APP, PREFERENZE } from "../costanti.ts";

export type StatoMotore = "attesa" | "pronto" | "assente";

export interface Filtri {
  motore: MotoreFiltri | null;
  stato: StatoMotore;
  /** id del filtro attivo (null = nessuno) */
  attivo: string | null;
  /** true quando all'avversario va il video con il filtro (altrimenti la fotocamera) */
  uscitaAttiva: boolean;
  /** il filtro scelto si è fermato per un errore: si mostra un avviso */
  errore: boolean;
  scegli(id: string | null): void;
  /** Filtri che chi gioca può usare adesso (per i cambi automatici durante il gioco). */
  usabili: readonly string[];
  /** Cambio automatico durante il gioco: NON cambia la scelta salvata. */
  cambiaPerGioco(id: string): void;
  /** Fine del gioco: si torna al filtro scelto da chi gioca. */
  ripristina(): void;
  /** Filtri Silver presenti nell'app che chi gioca NON può usare (per il «filtro a sorpresa» a fine partita). */
  sorprese: readonly string[];
  /** Fa provare per qualche secondo un filtro Silver a chi non ce l'ha (solo uno di `sorprese`). */
  provaSorpresa(id: string): void;
  /** v026: anteprima nello Store, su di sé (nessuno la vede): anche i filtri non ancora presi. */
  anteprima(id: string | null): void;
  /**
   * v046: id del filtro leggero appena messo da solo al posto di uno pesante (il telefono non ce la faceva): la
   * partita mostra un messaggio positivo breve. Torna null dopo CAMBIO_FLUIDO_MS (o con chiudiCambioFluido).
   */
  cambioFluido: string | null;
  chiudiCambioFluido(): void;
  /** v046: il filtro attivo è quello «di benvenuto» (chi gioca non ha mai scelto un filtro). */
  benvenuto: boolean;
}

/** v046: per quanto resta acceso `cambioFluido`. */
export const CAMBIO_FLUIDO_MS = 4000;

const NESSUNO = "-";

export function useFiltri(flusso: MediaStream): Filtri {
  const [motore, setMotore] = useState<MotoreFiltri | null>(null);
  const [stato, setStato] = useState<StatoMotore>("attesa");
  const [attivo, setAttivo] = useState<string | null>(null);
  const [uscitaAttiva, setUscitaAttiva] = useState(false);
  const [errore, setErrore] = useState(false);
  const [usabili, setUsabili] = useState<readonly string[]>([]);
  const [sorprese, setSorprese] = useState<readonly string[]>([]);
  const motoreRif = useRef<MotoreFiltri | null>(null);
  const richiesta = useRef(0);
  /** Filtri andati in errore in questa partita: non si ripropongono nei cambi automatici. */
  const guasti = useRef(new Set<string>());
  /** v046: filtro leggero messo al posto di uno pesante (per il messaggio) e il filtro di benvenuto di questa partita */
  const [cambioFluido, setCambioFluido] = useState<string | null>(null);
  const [benvenuto, setBenvenuto] = useState<string | null>(null);
  const benvenutoRif = useRef<string | null>(null);
  /** v046: tutti i filtri usabili (anche i pesanti), per scegliere il filtro leggero o di benvenuto */
  const tuttiUsabili = useRef<readonly string[]>([]);
  const timerFluido = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  /** v046: la funzione che passa a un filtro leggero (la chiama il motore da suAffanno; definita più sotto) */
  const suAffannoRif = useRef<(id: string) => void>(() => undefined);
  /** v046: pesi dei filtri caricati dall'indice (prima i cambi automatici non li conoscono) */
  const [pesiPronti, setPesiPronti] = useState(false);

  useEffect(() => {
    if (!leggiImpostazione(IMP_APP.filtriAttivi, true, eBooleano)) {
      setStato("assente");
      return;
    }
    let chiuso = false;
    let creato: MotoreFiltri | null = null;
    const n = (k: string, ripiego: number) => leggiImpostazione(k, ripiego, eNumero);
    // v046: i pesi dei filtri dall'indice (lo stesso file della scelta del filtro)
    void import("../filtri/indice/indice.json")
      .then((m) => {
        impostaPesi(m.default as unknown as VoceIndice[]);
      })
      .catch(() => undefined) // senza indice: tutti «medi», si gioca lo stesso
      .finally(() => !chiuso && setPesiPronti(true));
    void caricaAnalizzatori().then((fl) => {
      if (chiuso) return;
      if (!fl) {
        setStato("assente");
        return;
      }
      try {
        impostaAlzaTesta(n(IMP_APP.filtriAlzaTesta, ALZA_TESTA));
        const indizi = {
          processori: typeof navigator !== "undefined" ? navigator.hardwareConcurrency : undefined,
          memoriaGb: typeof navigator !== "undefined" ? (navigator as Navigator & { deviceMemory?: number }).deviceMemory : undefined,
          cpu: analisiSullaCpu,
        };
        impostaIndiziTelefono(indizi);
        const m = new MotoreFiltri({
          flusso,
          analizzatore: fl.io,
          orario: () => tempoReale.oraServer(),
          fps: n(IMP_APP.filtriFps, 30),
          // Senza filtro il proprio volto si analizza al ritmo del rilevamento (che alterna i due volti).
          analisiSenzaFiltro: n(IMP_APP.rilevamentoFps, 15) / 2,
          latoMassimo: n(IMP_APP.filtriLatoMassimo, 960),
          ripristinoBocca: n(IMP_APP.filtriRipristinoBocca, 0.6),
          margineBocca: n(IMP_APP.filtriMargineBocca, MARGINE_BOCCA),
          tieniVoltoMs: n(IMP_APP.filtriTieniVoltoMs, 300),
          stabilizza: {
            tagliaMinima: n(IMP_APP.filtriStabilizzaTaglia, STABILIZZA_PREDEFINITO.tagliaMinima),
            beta: n(IMP_APP.filtriStabilizzaBeta, STABILIZZA_PREDEFINITO.beta),
            tagliaDerivata: STABILIZZA_PREDEFINITO.tagliaDerivata,
          },
          erroriMassimi: n(IMP_APP.filtriErroriMassimi, 30),
          // v034: qualità automatica (telefoni lenti): parte più in basso se il telefono sembra vecchio
          qualita: {
            attiva: leggiImpostazione(IMP_APP.filtriQualitaAutomatica, true, eBooleano),
            iniziale: livelloIniziale(indizi),
            suCambio: (livello, l) => {
              (window as Window & { __qualitaFiltri?: unknown }).__qualitaFiltri = { livello, ...l };
            },
            // v046: anche alla qualità più bassa il telefono non ce la fa con questo filtro
            suAffanno: (id) => {
              if (!chiuso) suAffannoRif.current(id);
            },
          },
          suErrore: (e, id) => {
            segnalaErrore(new Error(`filtro ${id}: ${e instanceof Error ? e.message : String(e)}`));
            // Un filtro rotto non si ripropone alla prossima partita.
            if (leggiPreferenza(PREFERENZE.filtro) === id) salvaPreferenza(PREFERENZE.filtro, NESSUNO);
            if (id) {
              guasti.current.add(id);
              if (!chiuso) {
                setUsabili((u) => u.filter((x) => x !== id));
                setSorprese((u) => u.filter((x) => x !== id));
              }
            }
            if (!chiuso) {
              setAttivo(null);
              setErrore(true);
            }
          },
          suUscita: (a) => !chiuso && setUscitaAttiva(a),
          suGuasto: (e) => {
            segnalaErrore(e);
            if (chiuso) return;
            motoreRif.current = null;
            setMotore(null);
            setAttivo(null);
            setUscitaAttiva(false);
            setStato("assente");
          },
        });
        motoreRif.current = m;
        creato = m;
        setMotore(m);
        setStato("pronto");
      } catch (e) {
        // Telefono senza canvas.captureStream o simili: si gioca senza filtri.
        segnalaErrore(e);
        setStato("assente");
      }
    });
    return () => {
      chiuso = true;
      // v046: il livello a cui si è fermata la qualità in questa partita (conta solo se ha misurato abbastanza)
      if (creato) registraLivelloPartita(creato.qualita.livello, creato.qualita.misure);
      clearTimeout(timerFluido.current);
      motoreRif.current?.ferma();
      motoreRif.current = null;
      setMotore(null);
      setStato("attesa");
      setAttivo(null);
      setUscitaAttiva(false);
    };
  }, [flusso]);

  /** `prova`: filtro Silver fatto provare a chi non ha Silver (filtro a sorpresa), solo per pochi secondi. */
  const applica = useCallback(async (id: string | null, prova: boolean | "anteprima" = false) => {
    const m = motoreRif.current;
    if (!m) return;
    // Solo l'ultima scelta conta: una richiesta più vecchia che finisce dopo non deve vincere.
    const mia = ++richiesta.current;
    const ancoraMia = () => mia === richiesta.current && motoreRif.current === m;
    setErrore(false);
    if (id === null) {
      await m.scegli(null, null);
      if (ancoraMia()) setAttivo(null);
      return;
    }
    const catalogo = await filtriDisponibili();
    if (!ancoraMia()) return;
    const voce = catalogo.find((f) => f.id === id);
    // Filtro tolto dal catalogo o non più incluso nel piano: si torna a nessun filtro, senza errori.
    const accesso = voce ? accessoFiltro(voce, profiloFiltri()) : null;
    if (!voce || (accesso !== "libero" && prova !== "anteprima" && !(prova && accesso === "silver"))) {
      await m.scegli(null, null);
      if (ancoraMia()) setAttivo(null);
      return;
    }
    let f;
    try {
      f = await caricaFiltro(id);
    } catch (e) {
      // Codice del filtro non scaricato (rete): come un filtro in errore, con l'avviso.
      guasti.current.add(id);
      setUsabili((u) => u.filter((x) => x !== id));
      setSorprese((u) => u.filter((x) => x !== id));
      if (!ancoraMia()) return;
      segnalaErrore(e);
      setErrore(true);
      setAttivo(null);
      return;
    }
    if (!ancoraMia()) return;
    await m.scegli(f, voce.parametri);
    if (ancoraMia()) setAttivo(m.idFiltro);
  }, []);

  // Elenco per i cambi automatici: si rifà all'avvio del motore e quando cambia il piano (acquisto, scadenza).
  useEffect(() => {
    if (!motore) return;
    let vivo = true;
    const rifai = () =>
      filtriDisponibili()
        .then((c) => {
          if (!vivo) return;
          const ok = c.filter((f) => !guasti.current.has(f.id));
          const liberi = ok.filter((f) => accessoFiltro(f, profiloFiltri()) === "libero").map((f) => f.id);
          tuttiUsabili.current = liberi;
          // v046: per i cambi automatici, sui telefoni lenti niente filtri pesanti
          setUsabili(filtriConsigliati(liberi));
          setSorprese(ok.filter((f) => accessoFiltro(f, profiloFiltri()) === "silver").map((f) => f.id));
        })
        .catch(() => {
          if (!vivo) return;
          setUsabili([]);
          setSorprese([]);
        });
    void rifai();
    const togli = suCambioProfiloFiltri(() => void rifai());
    return () => {
      vivo = false;
      togli();
    };
  }, [motore, pesiPronti]);

  /** v046: mette un filtro leggero al posto di `id` e lo fa sapere alla partita (messaggio positivo). */
  const passaAFluido = useCallback(
    (id: string): boolean => {
      const nuovo = filtroFluidoSimile(id, tuttiUsabili.current);
      if (!nuovo) return false;
      if (benvenutoRif.current === id) {
        benvenutoRif.current = nuovo;
        setBenvenuto(nuovo);
      }
      void applica(nuovo).catch(() => undefined);
      setCambioFluido(nuovo);
      clearTimeout(timerFluido.current);
      timerFluido.current = setTimeout(() => setCambioFluido(null), CAMBIO_FLUIDO_MS);
      return true;
    },
    [applica],
  );

  useEffect(() => {
    suAffannoRif.current = (id: string) => {
      const m = motoreRif.current;
      if (!m || m.idFiltro !== id || !serveFiltroFluido(id, m.qualita.livello)) return;
      // da ora il telefono è «lento»: i cambi automatici evitano i filtri pesanti
      segnaAffanno();
      setUsabili((u) => filtriConsigliati(u));
      passaAFluido(id);
    };
  }, [passaAFluido]);

  // Al primo avvio del motore si rimette il filtro usato l'ultima volta (v046: se è pesante e il telefono è lento,
  // subito uno leggero simile, con il messaggio positivo; la scelta salvata resta).
  useEffect(() => {
    if (!motore || !pesiPronti) return;
    const salvato = leggiPreferenza(PREFERENZE.filtro);
    if (!salvato || salvato === NESSUNO) return;
    if (!filtroAdatto(salvato)) {
      void filtriDisponibili()
        .then((c) => {
          if (motoreRif.current !== motore || richiesta.current !== 0) return;
          const liberi = c.filter((f) => accessoFiltro(f, profiloFiltri()) === "libero").map((f) => f.id);
          if (!tuttiUsabili.current.length) tuttiUsabili.current = liberi;
          if (!passaAFluido(salvato)) void applica(salvato).catch(() => undefined);
        })
        .catch(() => void applica(salvato).catch(() => undefined));
      return;
    }
    void applica(salvato).catch(() => undefined);
  }, [motore, applica, pesiPronti, passaAFluido]);

  // v046: chi non ha mai scelto un filtro (nessuna preferenza salvata; "-" = ha scelto «nessuno» e si rispetta)
  // gioca con un filtro di benvenuto, appena si sa quali filtri può usare. Non si salva: resta «di benvenuto» finché
  // chi gioca non sceglie. Solo se nel frattempo non è stato messo nessun altro filtro.
  useEffect(() => {
    if (!motore || !pesiPronti || benvenutoRif.current || richiesta.current !== 0 || !usabili.length) return;
    if (leggiPreferenza(PREFERENZE.filtro) !== null) return;
    const id = filtroDiBenvenuto(tuttiUsabili.current.length ? tuttiUsabili.current : usabili);
    if (!id) return;
    benvenutoRif.current = id;
    setBenvenuto(id);
    void applica(id).catch(() => undefined);
  }, [motore, pesiPronti, usabili, applica]);

  const scegli = useCallback(
    (id: string | null) => {
      salvaPreferenza(PREFERENZE.filtro, id ?? NESSUNO);
      // v046: una scelta vera: il filtro di benvenuto non c'è più
      benvenutoRif.current = null;
      setBenvenuto(null);
      void applica(id).catch(() => undefined); // l'errore arriva già da suErrore
    },
    [applica],
  );

  const cambiaPerGioco = useCallback(
    (id: string) => {
      void applica(id).catch(() => undefined);
    },
    [applica],
  );
  const ripristina = useCallback(() => {
    const salvato = leggiPreferenza(PREFERENZE.filtro);
    // v046: senza una scelta salvata si torna al filtro di benvenuto (se c'era)
    const id = salvato && salvato !== NESSUNO ? salvato : salvato === null ? benvenutoRif.current : null;
    void applica(id).catch(() => undefined);
  }, [applica]);

  const chiudiCambioFluido = useCallback(() => {
    clearTimeout(timerFluido.current);
    setCambioFluido(null);
  }, []);

  const provaSorpresa = useCallback(
    (id: string) => {
      void applica(id, true).catch(() => undefined);
    },
    [applica],
  );

  const anteprima = useCallback(
    (id: string | null) => {
      void applica(id, "anteprima").catch(() => undefined);
    },
    [applica],
  );

  return {
    motore,
    stato,
    attivo,
    uscitaAttiva,
    errore,
    scegli,
    usabili,
    cambiaPerGioco,
    ripristina,
    sorprese,
    provaSorpresa,
    anteprima,
    cambioFluido,
    chiudiCambioFluido,
    benvenuto: benvenuto !== null && attivo === benvenuto,
  };
}
